import { useState, useEffect } from 'react';

const ZombieHandler = ({ user }) => {
    const [battery, setBattery] = useState(null);
    const [isZombie, setIsZombie] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    // 1. Feature Flag Check
    const active = user?.featureFlags?.zombieMode;
    // 2. Threshold Check (Propagated from /me or default)
    const threshold = user?.zombieModeThreshold || 15;

    useEffect(() => {
        if (!active) return;

        let batteryManager = null;

        const updateBattery = () => {
            if (batteryManager) {
                setBattery({
                    level: batteryManager.level * 100,
                    charging: batteryManager.charging
                });
            }
        };

        const initBattery = async () => {
            if ('getBattery' in navigator) {
                try {
                    batteryManager = await navigator.getBattery();
                    updateBattery();

                    batteryManager.addEventListener('levelchange', updateBattery);
                    batteryManager.addEventListener('chargingchange', updateBattery);
                } catch (e) {
                    console.warn('Battery API failed', e);
                }
            }
        };

        initBattery();

        return () => {
            if (batteryManager) {
                batteryManager.removeEventListener('levelchange', updateBattery);
                batteryManager.removeEventListener('chargingchange', updateBattery);
            }
        };
    }, [active]);

    // 3. Logic Trigger
    useEffect(() => {
        if (!battery) return;

        // Trigger if: Not charging AND Level < Threshold
        const shouldBeZombie = !battery.charging && battery.level <= threshold;

        if (shouldBeZombie && !dismissed) {
            setIsZombie(true);
            document.body.classList.add('zombie-mode');
        } else {
            setIsZombie(false);
            document.body.classList.remove('zombie-mode');
        }

    }, [battery, threshold, dismissed]);

    // Cleanup on unmount
    useEffect(() => {
        return () => document.body.classList.remove('zombie-mode');
    }, []);

    if (!isZombie) return null;

    return (
        <div className="fixed inset-0 z-[9999] pointer-events-none flex flex-col justify-end pb-24 px-4">
            {/* 
                We use pointer-events-none for the container so it doesn't block clicks, 
                but re-enable for the banner itself.
             */}
            <div className="bg-red-900/90 border border-red-500 text-red-100 p-4 rounded-xl shadow-[0_0_50px_rgba(220,38,38,0.5)] backdrop-blur-md animate-pulse pointer-events-auto flex items-center justify-between">
                <div>
                    <h3 className="font-black text-lg flex items-center gap-2 uppercase tracking-widest">
                        🧟 Brains... ({Math.round(battery?.level)}%)
                    </h3>
                    <p className="text-xs font-mono text-red-300">
                        Zombie Mode Active. UI Simplified to save power.
                    </p>
                </div>
                <button
                    onClick={() => setDismissed(true)}
                    className="p-2 px-4 bg-black/50 hover:bg-black/80 rounded-lg text-xs font-bold border border-red-500/50 uppercase tracking-wider transition"
                >
                    Dismiss
                </button>
            </div>

            <style>{`
                /* Global CSS Injection for Zombie Mode */
                body.zombie-mode {
                    background-color: #000 !important;
                    color: #ff0000 !important;
                }
                body.zombie-mode * {
                    border-color: #330000 !important;
                }
                body.zombie-mode img, 
                body.zombie-mode video,
                body.zombie-mode .avatar {
                    filter: grayscale(100%) contrast(150%) !important;
                    opacity: 0.5;
                }
                body.zombie-mode .bg-slate-900,
                body.zombie-mode .bg-neutral-900,
                body.zombie-mode .bg-white {
                    background-color: #050000 !important;
                }
            `}</style>
        </div>
    );
};

export default ZombieHandler;
